import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Modal, SegmentedControl, Sheet } from '@ai-tutor/ui';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { useDailyPlan } from '@/hooks/mindspace/useRevision';
import { useDomainTagOptions } from '@/hooks/mindspace/useMindspaceOptions';
import { SmartStartCard } from '@/components/features/mindspace/practice/widgets/SmartStartCard';
import { CustomSessionPanel } from '@/components/features/mindspace/practice/session/CustomSessionPanel';
import { TodaySummary } from '@/components/features/mindspace/practice/widgets/TodaySummary';
import { CustomSessionLauncher } from '@/components/features/mindspace/practice/session/CustomSessionLauncher';
import type {
  PracticeMode,
  PracticeScope,
  SessionConfig,
} from '@/lib/practice/session-types';
import {
  deriveStarterCounts,
  estimateMinutes,
  isReviewCleared,
  previewOf,
} from '@/lib/practice/starterPacks';

const DEFAULT_LENGTH = 20;

/**
 * §2.11 #31 — the mode switch is a radiogroup, not two toggle buttons.
 *
 * `SegmentedControl` from `@ai-tutor/ui` replaces a local `SlimModeToggle`
 * that reimplemented the same markup without the roving tabindex: the old one
 * announced correctly but put every segment in the tab order and offered no
 * arrow-key movement, which is the half of the radiogroup pattern that makes
 * it navigable.
 */
const MODE_OPTIONS = [
  { value: 'flashcard', label: 'Flashcards' },
  { value: 'quiz', label: 'Quiz' },
] as const satisfies readonly { value: PracticeMode; label: string }[];

interface PracticeSetupProps {
  mode: PracticeMode;
  onModeChange: (mode: PracticeMode) => void;
  onStart: (config: SessionConfig) => void;
}

export function PracticeSetup({
  mode,
  onModeChange,
  onStart,
}: PracticeSetupProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const phone = useMediaQuery('(max-width: 639.98px)');
  const closeCustom = () => {
    setCustomOpen(false);
    setCustomInitialDomain([]);
  };
  // Seed for the Custom-session modal's domain selection - set by the
  // quick-filter chips below the Custom block. Resets when the modal
  // closes so the next blank "Configure →" click starts fresh.
  const [customInitialDomain, setCustomInitialDomain] = useState<string[]>([]);

  const { data: plan, isLoading: planLoading } = useDailyPlan();
  const { data: domainOptions } = useDomainTagOptions();

  // Every starter card hydrates from this ONE daily-plan payload, so the grid
  // shimmers and reveals as a set instead of each card firing its own
  // `/practice/queue` preview and flipping state on its own clock. The
  // fallbacks, the `null`-vs-`0` distinction and the estimate arithmetic live
  // in `lib/practice/starterPacks` with a table — none of it is visible in a
  // screenshot, and all of it decides whether a card is clickable.
  const packs = plan?.starter_packs;
  const counts = deriveStarterCounts(plan);
  const estimate = (count: number | null | undefined) =>
    estimateMinutes(count, mode);
  const reviewEmpty = isReviewCleared(plan, planLoading, counts);

  // Single-shot guard against double-click races on smart-start cards
  // and Enter shortcut. Parent unmounts this component the moment
  // `onStart` triggers the phase change, so we never need to clear
  // the ref; the next mount gets a fresh `false`.
  const startingRef = useRef(false);
  const startScope = (scope: PracticeScope, lessonId?: string) => {
    if (startingRef.current) return;
    startingRef.current = true;
    const config: SessionConfig = {
      mode,
      scope,
      length: DEFAULT_LENGTH,
    };
    if (scope === 'recent' && lessonId) config.lessons = [lessonId];
    onStart(config);
  };

  const openCustomWithDomain = (domain: string) => {
    setCustomInitialDomain([domain]);
    setCustomOpen(true);
  };

  // Top 5 domain tags for the Custom block's quick-filter chips.
  // Sort by concept-count desc so the most-prominent tags lead, not
  // by the backend's incidental alphabetical aggregation order.
  const topDomainTags = useMemo(
    () =>
      [...(domainOptions ?? [])].sort((a, b) => b.count - a.count).slice(0, 5),
    [domainOptions]
  );

  return (
    <div className='flex h-full flex-col'>
      {/* Pinned header - slim mode toggle (left) + Today summary (right).
          Stays put while the Review / Practice sections below scroll, so the
          mode choice is always reachable. Vertical padding matches the
          History sidebar's "New session" button (pt-3 pb-2) so the mode
          toggle sits on the same level as it. */}
      <div className='flex-shrink-0 px-6 pb-2 pt-3 max-sm:px-4'>
        <div className='mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3'>
          <SegmentedControl
            label='Practice mode'
            options={MODE_OPTIONS}
            value={mode}
            onChange={onModeChange}
            className='max-sm:flex max-sm:w-full'
            segmentClassName='tap-target max-sm:flex-1'
          />
          {/* A phone says today's numbers in the page header's stats line. */}
          <div className='max-sm:hidden'>
            <TodaySummary />
          </div>
        </div>
      </div>

      {/* Scrollable body - the starter sections. */}
      <div className='min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4 max-sm:px-4 sm:pb-8'>
        <div className='mx-auto flex w-full max-w-6xl flex-col gap-8'>
          {/* REVIEW - spaced-repetition queue that clears as you go. When it's
          empty the cards stay (greyed at 0); the heading carries the
          "all caught up" note. */}
          <section>
            <SectionHeader
              title='Review'
              hint='Scheduled by spaced repetition. Clears as you practice.'
              shortHint='Clears as you practice.'
              aside={
                reviewEmpty ? (
                  <span className='inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'>
                    <Check className='h-3.5 w-3.5' />
                    All caught up
                  </span>
                ) : undefined
              }
            />
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6'>
              <SmartStartCard
                label='Due today'
                count={counts.due}
                estimatedMinutes={estimate(counts.due)}
                previewConcepts={previewOf(packs?.due)}
                subtitle='Scheduled for review now'
                recommended
                isLoading={planLoading}
                tone='indigo'
                disabled={!planLoading && (counts.due ?? 0) === 0}
                onClick={() => startScope('due')}
              />
              <SmartStartCard
                label='Weak concepts'
                count={counts.weak}
                estimatedMinutes={estimate(counts.weak)}
                previewConcepts={previewOf(packs?.weak)}
                subtitle='Low retention - needs work'
                isLoading={planLoading}
                tone='indigo'
                disabled={!planLoading && (counts.weak ?? 0) === 0}
                onClick={() => startScope('weak')}
              />
            </div>
          </section>

          {/* PRACTICE - repeatable drills; no-stakes (don't touch the schedule). */}
          <section>
            <SectionHeader
              title='Practice anytime'
              hint="Repeatable drills. Practice as much as you like; they don't change your review schedule."
              shortHint="Won't change your review schedule."
            />
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6'>
              <SmartStartCard
                label='Starred'
                count={counts.starred}
                estimatedMinutes={estimate(counts.starred)}
                previewConcepts={previewOf(packs?.starred)}
                isLoading={planLoading}
                subtitle='Concepts you saved'
                disabled={!planLoading && (counts.starred ?? 0) === 0}
                tone='slate'
                onClick={() => startScope('starred')}
              />
              <SmartStartCard
                label='Recent lesson'
                count={counts.recent}
                estimatedMinutes={estimate(counts.recent)}
                previewConcepts={previewOf(packs?.recent)}
                subtitle={counts.recentLessonTitle ?? 'No lessons yet'}
                isLoading={planLoading}
                disabled={
                  !planLoading &&
                  (!counts.recentLessonId || (counts.recent ?? 0) === 0)
                }
                tone='slate'
                onClick={() =>
                  counts.recentLessonId &&
                  startScope('recent', counts.recentLessonId)
                }
              />
            </div>

            <CustomSessionLauncher
              mode={mode}
              topDomainTags={topDomainTags}
              onConfigure={() => {
                setCustomInitialDomain([]);
                setCustomOpen(true);
              }}
              onConfigureWithDomain={openCustomWithDomain}
            />
          </section>
        </div>
      </div>

      {/* A phone gets the wizard as a tall bottom sheet, as the app's other
          phone panels are, rather than a box floating 16px in from each
          edge with the page dimmed around it. */}
      {phone ? (
        <Sheet
          isOpen={customOpen}
          onClose={closeCustom}
          title='Custom session'
          size='tall'
        >
          <CustomSessionPanel
            initialLength={DEFAULT_LENGTH}
            initialDomain={customInitialDomain}
            onStart={partial => {
              setCustomOpen(false);
              setCustomInitialDomain([]);
              onStart({ mode, ...partial });
            }}
          />
        </Sheet>
      ) : (
        <Modal
          isOpen={customOpen}
          onClose={closeCustom}
          title='Custom session'
          size='xl'
        >
          <CustomSessionPanel
            initialLength={DEFAULT_LENGTH}
            initialDomain={customInitialDomain}
            onStart={partial => {
              setCustomOpen(false);
              setCustomInitialDomain([]);
              onStart({ mode, ...partial });
            }}
          />
        </Modal>
      )}
    </div>
  );
}

/**
 * Section heading + one-line hint, used to separate "Review" (depletes)
 * from "Practice anytime" (repeatable) so the two behaviours read clearly.
 */
function SectionHeader({
  title,
  hint,
  shortHint,
  aside,
}: {
  title: string;
  hint: string;
  /** A phone's one line, where the full hint ran to two. */
  shortHint?: string;
  aside?: ReactNode;
}) {
  return (
    <div className='mb-3 flex items-start justify-between gap-3'>
      <div className='min-w-0'>
        <h3 className='text-sm font-semibold text-foreground max-sm:font-mono max-sm:text-xs max-sm:font-medium max-sm:uppercase max-sm:tracking-[0.14em] max-sm:text-muted-foreground'>
          {title}
        </h3>
        <p className='mt-0.5 text-xs text-muted-foreground'>
          {shortHint ? (
            <>
              <span className='max-sm:hidden'>{hint}</span>
              <span className='sm:hidden'>{shortHint}</span>
            </>
          ) : (
            hint
          )}
        </p>
      </div>
      {aside && <div className='flex-shrink-0 pt-0.5'>{aside}</div>}
    </div>
  );
}
